import { Empty } from 'antd';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import moment from 'moment';
import numeral from 'numeral';
import React, { useEffect } from 'react';
import { formatByte2Mb, formatNum } from '@/infrastructure/utils/digit';
import { HH_MM_SS } from '@/infrastructure/utils/timestamp';
import { RATE_LIST } from '@/presentation/components/issue-trend-analysis';
import { MetricName } from '@/presentation/components/trend/api';
import { IssueTimeType } from '@/proto/issue';
import { IGetDashboardDataRsp, MetricSubType, TimeType } from '@/proto/metric';
import { fromPBTime } from '@raft/utils/src/date';
import { useTranslation } from 'react-i18next';


interface IProps {
  loading: boolean;
  chartData?: IGetDashboardDataRsp;
  compareChartData?: IGetDashboardDataRsp;
  unit: number;
  timeWindow?: number;
  compareTimeWindow?: number;
  tabKey: number;
  tabName: string;
}
export const SYMBOL = ['%', '‰', '‱', 'MB'];
export const RATE = [100, 1000, 10000];

export const Chart: React.FC<IProps> = (props: IProps) => {
  const { t } = useTranslation();
  const { loading,
    timeWindow,
    chartData,
    tabKey,
    unit,
    tabName,
    compareChartData,
    compareTimeWindow,
  } = props;
  let chartRef: any =  null;
  echarts.use([CanvasRenderer, LegendComponent, TooltipComponent, GridComponent, LineChart]);

  useEffect(() => {
    const options = getChartOptions();
    chartRef?.setOption({ ...options, series: {} }, true);
    chartRef?.setOption(options, true);
  }, [timeWindow, chartData, tabKey, unit]);

  const getSeries = (chartData: IGetDashboardDataRsp | undefined): string[] => {
    const defaultZero = '0';
    switch (tabKey) {
      case MetricSubType.SUB_TYPE_COUNT_RATIO: { // 次数异常率
        const affectCount = chartData?.data?.find(item => item.metricName === MetricName.CountRatio)?.dataPoints || [];
        return  affectCount.map((item: number) => {
          if (item > 0) {
            const rate = RATE[unit];
            return ((item * rate)).toFixed(4);
          }
          return defaultZero;
        });
      }
      case MetricSubType.SUB_TYPE_AVG_DEVICE_RATIO: { // 设备平均异常率
        const affectCount = chartData?.data?.find(item => item.metricName === MetricName.avgDeviceUvRatio)
          ?.dataPoints || [];
        return  affectCount.map((item: number) => {
          if (item > 0) {
            const rate = RATE[unit];
            return ((item * rate)).toFixed(4);
          }
          return defaultZero;
        });
      }
      case MetricSubType.SUB_TYPE_DEVICE_RATIO: { // 设备异常率
        const affectDeviceCount = chartData?.data?.find(item => item.metricName === MetricName.DeviceRatio)
          ?.dataPoints || [];
        return affectDeviceCount.map((item: number) => {
          if (item > 0) {
            const rate = RATE[unit];
            return ((item * rate)).toFixed(4);
          }
          return defaultZero;
        });
      }
      case MetricSubType.SUB_TYPE_USER_RATIO: { // 用户异常率
        const affectDeviceCount = chartData?.data?.find(item => item.metricName === MetricName.UserUvRatio)
          ?.dataPoints || [];
        return affectDeviceCount.map((item: number) => {
          if (item > 0) {
            const rate = RATE[unit];
            return ((item * rate)).toFixed(4);
          }
          return defaultZero;
        });
      }
      case MetricSubType.SUB_TYPE_COUNT: { // 发生次数
        return chartData?.data?.find(item => item?.metricName === MetricName.Count)
          ?.dataPoints
          ?.map?.(item => (item > 0 ? String(item) : defaultZero))
          || [];
      }
      case MetricSubType.SUB_TYPE_LAUNCH_COUNT: { // 启动次数
        return chartData?.data?.find(item => item?.metricName === MetricName.DauLaunchTimes)
          ?.dataPoints
          ?.map?.(item => (item > 0 ? String(item) : defaultZero))
          || [];
      }
      case MetricSubType.SUB_TYPE_DEVICE_UV: { // 影响设备数
        return chartData?.data?.find(item => item.metricName === MetricName.DeviceUv)
          ?.dataPoints
          ?.map?.(item => (item > 0 ? String(item) : defaultZero))
          || [];
      }
      case MetricSubType.SUB_TYPE_USER_UV: {
        return chartData?.data?.find(item => item.metricName === MetricName.UserUv)
          ?.dataPoints
          ?.map?.(item => (item > 0 ? String(item) : defaultZero))
          || [];
      }
      case MetricSubType.SUB_TYPE_DAU_DEVICE_UV: {
        return chartData?.data?.find(item => item.metricName === MetricName.DauDeviceUv)
          ?.dataPoints
          ?.map?.(item => (item > 0 ? String(item) : defaultZero))
          || [];
      }
      case MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION: {
        return chartData?.data?.find(item => item.metricName === MetricName.TrafficExceptionSum)
          ?.dataPoints
          ?.map?.(item => (item > 0 ? numeral(item).format('0.00') : defaultZero))
          || [];
      }
      default:
        return [];
    }
  };

  const formatData = () => {
    const xAxisData = (chartData?.timePoints || []).map(timePoint => fromPBTime(timePoint).unix()) || [];
    const compareXAxisData = (compareChartData?.timePoints
      || []).map(timePoint => fromPBTime(timePoint).unix()) || [];
    let xAxisUnix = xAxisData;
    let compareXAxisUnix = compareXAxisData;

    const seriesData: string[] = getSeries(chartData);
    const compareSeriesData: string[] = getSeries(compareChartData);
    // 相同聚合 但日期不相同则补充数据

    if (compareChartData
      && timeWindow === compareTimeWindow && xAxisData?.length && compareXAxisData?.length
      && (xAxisData?.[0] !== compareXAxisData?.[0]
        || xAxisData?.[xAxisData?.length - 1] !== compareXAxisData?.[compareXAxisData?.length - 1])
    ) {
      // 时间范围小于等于1天，则使用时间范围最大的作为X轴
      if ((timeWindow === IssueTimeType.TIME_TYPE_MINUTE5 || timeWindow === IssueTimeType.TIME_TYPE_HOUR)
        && ((moment.unix(Number(xAxisUnix?.[xAxisUnix?.length - 1])).diff(moment.unix(Number(xAxisUnix?.[0])), 'day') < 1) && (moment.unix(Number(compareXAxisUnix?.[compareXAxisUnix?.length - 1])).diff(moment.unix(Number(compareXAxisUnix?.[0])), 'day') < 1))
      ) {
        xAxisUnix = xAxisData.length > compareXAxisData.length ? xAxisData : compareXAxisData;
        compareXAxisUnix = xAxisUnix;

        const seriesDataUnshiftIndex = xAxisUnix.findIndex(value => moment.unix(value).format(HH_MM_SS)
          === moment.unix(xAxisData[0]).format(HH_MM_SS));
        const seriesDataPushIndex = (xAxisUnix.length
          - xAxisUnix.findIndex(value => moment.unix(value).format(HH_MM_SS)
            === moment.unix(xAxisData[xAxisData.length - 1]).format(HH_MM_SS))) - 1;
        seriesData.unshift(...Array(seriesDataUnshiftIndex >= 0 ? seriesDataUnshiftIndex : 0).fill(undefined));
        seriesData.push(...Array(seriesDataPushIndex >= 0 ? seriesDataPushIndex : 0).fill(undefined));

        const compareSeriesDataUnshiftIndex = compareXAxisUnix.findIndex(value => moment.unix(value).format(HH_MM_SS)
          === moment.unix(compareXAxisData[0]).format(HH_MM_SS));
        const compareSeriesDataPushIndex = (compareXAxisUnix.length
          - compareXAxisUnix.findIndex(value => moment.unix(value).format(HH_MM_SS)
            === moment.unix(compareXAxisData[compareXAxisData.length - 1]).format(HH_MM_SS)) - 1);
        compareSeriesData.unshift(...Array(compareSeriesDataUnshiftIndex >= 0
          ? compareSeriesDataUnshiftIndex : 0).fill(undefined));
        compareSeriesData.push(...Array(compareSeriesDataPushIndex >= 0
          ? compareSeriesDataPushIndex : 0).fill(undefined));
      } else {
        const differenceMax = Math.max(xAxisUnix?.length, compareXAxisUnix?.length);
        const differenceMin = Math.min(xAxisUnix?.length, compareXAxisUnix?.length);
        const difference = differenceMax - differenceMin;
        if (xAxisUnix?.length === differenceMin) {
          const diffList = Array.from(
            Array(difference),
            (_i, index) => moment.unix(xAxisUnix?.[xAxisUnix?.length - 1]).add(index + 1, 'days')
              .unix(),
          );
          xAxisUnix.push(...diffList);
        }
        if (compareXAxisUnix?.length === differenceMin) {
          const compareDiffList = Array.from(
            Array(difference),
            (_i, index) => moment.unix(compareXAxisUnix?.[compareXAxisUnix?.length - 1]).add(index + 1, 'days')
              .unix(),
          );
          compareXAxisUnix.push(...compareDiffList);
        }
        const seriesDataUnshiftIndex = xAxisUnix.findIndex(value => value === xAxisData[0]);
        const seriesDataPushIndex = (xAxisUnix.length
          - xAxisUnix.findIndex(value => value === xAxisData[xAxisData.length - 1])) - 1;
        seriesData.unshift(...Array(seriesDataUnshiftIndex >= 0 ? seriesDataUnshiftIndex : 0).fill(undefined));
        seriesData.push(...Array(seriesDataPushIndex >= 0 ? seriesDataPushIndex : 0).fill(undefined));

        const compareSeriesDataUnshiftIndex = compareXAxisUnix.findIndex(value => value
          === compareXAxisData[0]);
        const compareSeriesDataPushIndex = (compareXAxisUnix.length
          - compareXAxisUnix.findIndex(value => value
            === compareXAxisData[compareXAxisData.length - 1]) - 1);
        compareSeriesData.unshift(...Array(compareSeriesDataUnshiftIndex >= 0
          ? compareSeriesDataUnshiftIndex : 0).fill(undefined));
        compareSeriesData.push(...Array(compareSeriesDataPushIndex >= 0
          ? compareSeriesDataPushIndex : 0).fill(undefined));
      }
    }

    const xAxis = [{
      type: 'category',
      axisTick: false,
      splitLine: {
        show: false,
      },
      axisLabel: {
        interval: xAxisUnix.length > 200 ? 30 : 'auto',
        textStyle: {
          color: '#7B889C',
        },
        formatter(value: string) {
          switch (timeWindow) {
            case IssueTimeType.TIME_TYPE_DAY:
              return moment.unix(Number(value)).format('MM-DD');
            case IssueTimeType.TIME_TYPE_HOUR:
              return moment.unix(Number(value)).format('HH');
            case TimeType.WEEK:
              return `${moment.unix(Number(value)).format('MM-DD')}~${moment.unix(Number(value)).add(6, 'days')
                .format('MM-DD')}`;
            case TimeType.MONTH:
              return moment.unix(Number(value)).format('YY-MM');
            default:
              return moment.unix(Number(value)).format('HH:mm');
          }
        },
      },
      data: xAxisUnix,
      axisLine: {
        lineStyle: {
          color: '#DCE3EE',
        },
      },
    },
    ];
    // 获取legend 图例


    const ySuffix = RATE_LIST.includes(tabKey) ? SYMBOL[unit] : '';

    const series = [{
      id: 'lineA',
      smooth: timeWindow !== IssueTimeType.TIME_TYPE_MINUTE5,
      xAxisIndex: 0,
      name: t("条件A"),
      data: seriesData,
      type: 'line',
      symbolSize: 7,
      showAllSymbol: false,
      itemStyle: {
        borderWidth: 16,
        borderType: 'solid',
      },
      markPoint: {
        id: 'rect',
        animation: false,
        symbol: 'rect',
        symbolSize: [16, 2560],
        symbolOffset: [0, '-50%'],
        label: {
          show: false,
        },
        itemStyle: {
          color: 'rgba(0, 0, 0, 0)',
        },
        data: [],
      },
      endLabel: {
        position: 'top',
        distance: -18,
        offset: [0, -10],
        show: true,
        formatter: (params: any) => {
          const date = moment.unix(Number(params.name));
          const momentDate = moment();
          if (timeWindow === TimeType.WEEK) {
            const weekDate = moment.unix(Number(params.name)).add(6, 'days');
            if (params.dataIndex === seriesData.length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
              return t("统计中");
            }
          }
          if (timeWindow === TimeType.MONTH) {
            if (params.dataIndex === seriesData.length - 1 && date.isSameOrAfter(momentDate, 'month')) {
              return t("统计中");
            }
          }
          if (params.dataIndex === seriesData.length - 1 && momentDate.isSame(date, 'day')) {
            if (timeWindow === TimeType.DAY) {
              return t("统计中");
            }
            if (timeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
              return t("统计中");
            }
            if (timeWindow === TimeType.MINUTE5) {
              let minutes: string | string[] = momentDate.format('mm').split('');
              minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
              const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
              if (date.isSame(startsTime)) {
                return t("统计中");
              }
              return '';
            }
          }

          return '';
        },
      },
    }];
    if (compareChartData) {
      series.push({
        ...series[0],
        xAxisIndex: 1,
        data: compareSeriesData,
        id: 'lineB',
        smooth: compareTimeWindow  !== IssueTimeType.TIME_TYPE_MINUTE5,
        name: t("条件B"),
        itemStyle: {
          borderWidth: 16,
          borderType: 'solid',
        },
        endLabel: {
          position: 'top',
          distance: -18,
          offset: [0, -10],
          show: true,
          formatter: (params: any) => {
            const date = moment.unix(Number(params.name));
            const momentDate = moment();
            if (compareTimeWindow  === TimeType.WEEK) {
              const weekDate = moment.unix(Number(params.name)).add(6, 'days');
              if (params.dataIndex === compareSeriesData.length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
                return t("统计中");
              }
            }
            if (compareTimeWindow === TimeType.MONTH) {
              if (params.dataIndex === compareSeriesData.length - 1 && date.isSameOrAfter(momentDate, 'month')) {
                return t("统计中");
              }
            }
            if (params.dataIndex === compareSeriesData.length - 1 && momentDate.isSame(date, 'day')) {
              if (compareTimeWindow === TimeType.DAY) {
                return t("统计中");
              }
              if (compareTimeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
                return t("统计中");
              }
              if (compareTimeWindow === TimeType.MINUTE5) {
                let minutes: string | string[] = momentDate.format('mm').split('');
                minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
                const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
                if (date.isSame(startsTime)) {
                  return t("统计中");
                }
                return '';
              }
            }

            return '';
          },
        },
      });
      xAxis.push({
        ...xAxis[0],
        data: compareXAxisUnix,
        axisLabel: {
          interval: compareXAxisUnix.length > 200 ? 30 : 'auto',
          textStyle: {
            color: '#7B889C',
          },
          formatter(value: string) {
            switch (compareTimeWindow) {
              case IssueTimeType.TIME_TYPE_DAY:
                return moment.unix(Number(value)).format('MM-DD');
              case IssueTimeType.TIME_TYPE_HOUR:
                return moment.unix(Number(value)).format('HH');
              case TimeType.WEEK:
                return `${moment.unix(Number(value)).format('MM-DD')}~${moment.unix(Number(value)).add(6, 'days')
                  .format('MM-DD')}`;
              case TimeType.MONTH:
                return moment.unix(Number(value)).format('YY-MM');
              default:
                return moment.unix(Number(value)).format('HH:mm');
            }
          },
        },
      });
    }
    return {
      xAxis,
      ySuffix,
      series,
    };
  };
  const getValue = (value: number) => (RATE_LIST.includes(tabKey)
    ? `${value}${SYMBOL[unit]}` : formatNum(value || 0));
  const getChartOptions = (): EChartsOption => {
    const data = formatData();

    return  {
      color: ['#0A6CFF', '#52B7FF'],
      animationDuration: 100,
      legend: {
        show: !!compareChartData,
        bottom: 0,
        data: [t("条件A"), t("条件B")],
      },
      grid: {
        left: 10,
        top: 20,
        bottom: 40,
        right: 30,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          show: true,
          type: 'line',
          lineStyle: {
            width: 16,
            type: 'solid',
            color: 'rgba(194, 218, 255, 0.5)',
            shadowStyle: {
              color: 'rgba(194, 218, 255, 0.5)',
            },
          },
        },
        formatter(params: any) {
          const isTimeTypeHour = IssueTimeType.TIME_TYPE_HOUR === timeWindow;

          const momentDate = moment.unix(params[0]?.name);
          let displayValue = getValue(params[0]?.data);
          if (tabKey === MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION) {
            displayValue = `${formatByte2Mb(parseFloat(displayValue) || 0)}MB`;
          }
          let str = '';
          if (params[0]?.data !== undefined) {
            if (timeWindow === TimeType.WEEK) {
              str = `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days')
                .format('MM-DD')}</br>
          ${params[0].marker}${tabName}：${displayValue}`;
            } else if (timeWindow === TimeType.MONTH) {
              str = `${momentDate.format('YY-MM')}</br>
          ${params[0].marker}${tabName}：${displayValue}`;
            } else {
              const timePart = IssueTimeType.TIME_TYPE_DAY === timeWindow
                ? momentDate.format('YYYY-MM-DD')
                : t('统计区间：[{{start}}, {{end}})', {
                  start: momentDate.format('HH:mm'),
                  end: momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm'),
                });
              str = t('{{timePart}}</br>{{marker}}{{tabName}}：{{value}}', {
                timePart,
                marker: params[0].marker,
                tabName,
                value: displayValue,
              });
            }
          }


          if (params[1]?.data !== undefined) {
            if (str) {
              str += '</br></br>';
            }
            const isTimeTypeHour = IssueTimeType.TIME_TYPE_HOUR === compareTimeWindow;
            const momentDate = moment.unix(params[1]?.name);
            let displayValue = getValue(params[1]?.data);
            if (tabKey === MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION) {
              displayValue = `${formatByte2Mb(parseFloat(displayValue) || 0)}MB`;
            }
            if (compareTimeWindow === TimeType.WEEK) {
              str +=  `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days')
                .format('MM-DD')}</br>
          ${params[1].marker}${tabName}：${displayValue}`;
            } else if (compareTimeWindow === TimeType.MONTH) {
              str +=  `${momentDate.format('YY-MM')}</br>
          ${params[1].marker}${tabName}：${displayValue}`;
            } else {
              const timePart = IssueTimeType.TIME_TYPE_DAY === compareTimeWindow
                ? momentDate.format('YYYY-MM-DD')
                : t('统计区间：[{{start}}, {{end}})', {
                  start: momentDate.format('HH:mm'),
                  end: momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm'),
                });
              str += t('{{timePart}}</br>{{marker}}{{tabName}}：{{value}}', {
                timePart,
                marker: params[1].marker,
                tabName,
                value: displayValue,
              });
            }
          }
          return  str;
        },
      },

      xAxis: data.xAxis,
      yAxis: {
        type: 'value',
        max(value: Record<string, any>) {
          return RATE_LIST.includes(tabKey) ?  (value.max * 1.2).toFixed(4) : undefined;
        },
        min(value: Record<string, any>) {
          return RATE_LIST.includes(tabKey) ? (value.min * 0.8).toFixed(4) : undefined;
        },
        axisLabel: {
          formatter: data.ySuffix ? `{value} ${data.ySuffix}` : '{value}',
          textStyle: {
            color: '#7B889C',
          },
        },
      },
      series: data.series,
    };
  };


  if (!loading && (chartData?.data?.length || 0) === 0) {
    return <Empty style={{ width: '100%',
      height: 262,
      margin: '0 0 20px 0',
      border: '1px solid rgba(5, 5, 5, 0.06)',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
    }}/>;
  }
  return <div>
    <ReactECharts ref={(e: any) => {
      chartRef = e?.getEchartsInstance();
    }} showLoading={loading}  echarts={echarts}  option={{}} style={{ width: '100%', height: 262, marginBottom: 20 }} />
  </div>;
};

